import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { Stack, Text, Header, Button, Loader } from '@crossfit/ui'

import { Teammate } from 'types'
import Modal from 'components/Modal'
import * as affiliatesService from 'services/affiliates'
import { useNotification } from 'components/Notification'
import { useSelector } from 'react-redux'
import { getAffiliate } from 'store/selectors'

type Props = {
  onClose: () => void
  teammate: Teammate
}

function useLeaveTeam({ teammate }: Omit<Props, 'onClose'>) {
  const { id } = teammate
  const affiliate = useSelector(getAffiliate)
  const navigate = useNavigate()
  const form = useForm()
  const queryClient = useQueryClient()
  const showNotification = useNotification()

  const removeTeammate = useMutation(() =>
    affiliatesService.removeTeammate({
      userId: id,
      affiliateId: affiliate?.id,
    }),
  )

  async function handleSubmit() {
    await removeTeammate.mutateAsync()
    await queryClient.refetchQueries('members')
    await queryClient.refetchQueries('affiliate')
    showNotification(`You have left the team`)
    navigate('/')
  }

  return {
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

function LeaveTeam({ onClose, teammate }: Props) {
  const { onSubmit, isSubmitting } = useLeaveTeam({ teammate })
  const affiliate = useSelector(getAffiliate)

  return (
    <Modal
      onClose={onClose}
      header={
        <Header size='small' as='h3'>
          Leave Team?
        </Header>
      }
    >
      {isSubmitting ? (
        <Loader />
      ) : (
        <form onSubmit={onSubmit}>
          <Text mb={8}>
            If you leave the <strong>{affiliate?.name}</strong> team, you’ll no
            longer have access to any of these tools. To regain access, you’ll
            need to be invited back to the team by the owner.
          </Text>
          <Stack
            direction='horizontal'
            style={{ justifyContent: 'flex-end' }}
            spacing={4}
          >
            <Button type='button' onClick={onClose} variant='secondary'>
              Cancel
            </Button>
            <Button type='submit' variant='destructive'>
              Leave Team
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  )
}

export default LeaveTeam
